/**
 * useMobilePreviewMode
 *
 * Shared mobile preview-mode wiring for SlidePanel-based detail/config panels,
 * mirroring the authenticated map (`applyDetailsPreviewOnMobile`): on mobile the
 * panel opens in partial preview (map stays visible on top) instead of full
 * height, and swipe-up expands it to full.
 *
 * Used by the map configurator (`useMapConfigOverlays`) and the public artifact
 * detail panel, both of which are URL-driven and never go through
 * `slice.openDetails()`, so the store's `slidePanel.previewMode` flag is never
 * set there — this hook replicates just that side effect. `unregisterSlidePanel`
 * resets `previewMode` when the panel closes, so no cleanup is needed here.
 *
 * @module hooks/useMobilePreviewMode
 */
import { useLayoutEffect } from 'react';

import { useGeoMarkupStore } from '@/store';

/** Store-backed mobile preview state for a SlidePanel-based panel. */
export interface UseMobilePreviewMode {
  /** Whether the panel is in partial preview (store-backed, mobile only). */
  previewMode: boolean;
  /** Toggle preview mode — wired to the SlidePanel swipe gestures. */
  setPreviewMode: (isPreview: boolean) => void;
}

/**
 * @param isMobile - From `useDeviceLayout`; preview only applies on mobile.
 * @param openKey - Key of the currently-open panel/artifact; `null` means closed
 *   (do not force preview). Omit for single-panel pages that are always "open"
 *   while mounted. Keyed on the id so switching artifact A → B re-enters preview.
 */
export function useMobilePreviewMode(
  isMobile: boolean,
  openKey?: string | null
): UseMobilePreviewMode {
  const previewMode = useGeoMarkupStore(
    (state) => state.ui.slidePanel.previewMode
  );
  const setPreviewMode = useGeoMarkupStore((state) => state.ui.setPreviewMode);

  // useLayoutEffect runs before paint, avoiding a flash of the full-height panel
  // (or hidden overlays) before it settles into preview. Re-runs when the open
  // panel changes, so switching artifact A → B returns to preview.
  useLayoutEffect(() => {
    if (isMobile && openKey !== null) {
      setPreviewMode(true);
    }
  }, [isMobile, openKey, setPreviewMode]);

  return { previewMode, setPreviewMode };
}
